// The banner (07-frame.md, v1.58.37, full-bleed; 18-channel-settings.md, Banner): a picture filling it
// (cover) under a scrim, else the circle tile's gradient with a soft white light under a veil. The side
// list shows it on top of every section; Space and Circle settings preview it at its real size (v1.58.39).
import { Picture } from '../blocks/Bits';
import type { MatrixClient, Room } from 'matrix-js-sdk';
import { useEffect, useRef, useState, type CSSProperties, type ReactNode, type Ref } from 'react';
import { Button } from '../blocks/Button';
import { Icon, icons } from '../blocks/icons';
import { useAction } from '../blocks/Wait';
import { circleBanner, circleName } from '../accounts/accounts';
import { idColour } from '../core/format';
import { server } from '../core/server';
import { serverOf } from '../core/session';
import { t } from '../i18n';
import { Card, Section } from '../settings/frame';
import { openRoomSettings } from '../settings/RoomSettings';
import { measure, PANEL, USUAL } from './scrim';

const BANNER = 'chat.desyr.banner';
const TYPES = ['image/png', 'image/jpeg', 'image/webp'];
export const mayBanner = (client: MatrixClient, space: Room) => space.currentState.maySendStateEvent(BANNER, client.getUserId()!);
/** "Change banner…" (07): Space settings, General, scrolled to Banner. */
let wanted = false;
export const openBanner = (space: Room) => ((wanted = true), openRoomSettings(space));
export const urlOf = (space: Room): string | undefined => space.currentState.getStateEvents(BANNER, '')?.getContent().url;
/** The circle's ID colour, the banner's gradient (as the circle tile's). */
export const circleTone = (client: MatrixClient) => `var(--name-${idColour(serverOf(client.getUserId()!))})`;

/** The circle's main space (07, v1.58.32): the oldest "Everyone in <Circle>" space joined (not federated, public join rule, 09). */
export function mainSpace(client: MatrixClient): Room | undefined {
  const create = (r: Room) => r.currentState.getStateEvents('m.room.create', '');
  return client
    .getRooms()
    .filter((r) => r.isSpaceRoom() && r.getMyMembership() === 'join' && r.getJoinRule() === 'public' && create(r)?.getContent()['m.federate'] === false)
    .sort((x, y) => (create(x)?.getTs() ?? 0) - (create(y)?.getTs() ?? 0))[0];
}

/**
 * What the side list's banner shows (07, design A; v1.58.37): in a space its name, banner and joined
 * members; elsewhere the circle's name, its own banner, else its main space's, else the gradient
 * (v1.58.32), with the main space's joined members.
 */
export function cardOf(client: MatrixClient, space?: Room): { name: string; url?: string; from: 'circle' | 'space' | 'gradient'; members?: number } {
  const me = client.getUserId()!;
  const main = space ?? mainSpace(client);
  const own = space ? undefined : (circleBanner(me) ?? undefined);
  const url = own ?? (main && urlOf(main));
  return { name: space ? space.name : circleName(me), url, from: !url?.startsWith('mxc://') ? 'gradient' : own ? 'circle' : 'space', members: main?.getJoinedMemberCount() };
}

/** A space's banner, or with `circle` (where its picture comes from) the circle's on the phone's Home tab (07, v1.58.44). */
export function Banner({ client, url, circle, ref, hook, inert, children }: { client: MatrixClient; url?: string; circle?: 'circle' | 'space' | 'gradient'; ref?: Ref<HTMLDivElement>; hook?: Record<string, string>; inert?: boolean; children?: ReactNode }) {
  const src = url?.startsWith('mxc://') ? client.mxcUrlToHttp(url, 960, 400, 'scale', false, true, server.features.authenticatedMedia) : undefined;
  // The gradient until the picture has loaded, and when it fails (02, "Pictures that don't load").
  const [shown, setShown] = useState(false);
  // Measured once shown: a deeper scrim under the text, a dark "⋯" backing, where the picture is light (01, v1.58.48).
  const box = useRef<HTMLDivElement | null>(null);
  const [look, setLook] = useState<{ scrim?: number; light: boolean }>();
  useEffect(() => {
    setLook(undefined);
    const at = box.current;
    if (!shown || !src || !at) return;
    let live = true;
    void measure(src, at.clientWidth, at.clientHeight, at.parentElement?.classList.contains('side-scroll') || at.dataset.desyrBannerPreview === 'computer' ? PANEL : USUAL).then((l) => live && setLook(l));
    return () => void (live = false);
  }, [shown, src]);
  const pictured = shown && src;
  return (
    <div
      ref={(el) => {
        box.current = el;
        if (typeof ref === 'function') ref(el);
        else if (ref) ref.current = el;
      }}
      inert={inert}
      className={`banner${pictured ? ' pictured' : ''}${pictured && look?.scrim ? ' deep' : ''}${pictured && look?.light ? ' light-top' : ''}`}
      {...(circle ? { 'data-desyr-circle-banner': shown && src ? circle : 'gradient' } : { 'data-desyr-banner': shown && src ? 'picture' : 'gradient' })}
      style={{ '--circle-tone': circleTone(client), ...(look?.scrim && { '--scrim': look.scrim }) } as CSSProperties}
      {...hook}
    >
      <Picture src={src ?? undefined} alt="" onShown={setShown} />
      {children}
    </div>
  );
}

export function BannerSection({ client, room }: { client: MatrixClient; room: Room }) {
  const at = useRef<HTMLDivElement>(null);
  useEffect(() => void (wanted && ((wanted = false), at.current?.scrollIntoView({ block: 'start' }))), []);
  if (!room.isSpaceRoom()) return null;
  return (
    <div ref={at}>
      <BannerCard
        client={client}
        preview={<BannerPreviews client={client} card={cardOf(client, room)} />}
        has={!!urlOf(room)}
        may={mayBanner(client, room)}
        set={(url) => client.sendStateEvent(room.roomId, BANNER as never, (url ? { url } : {}) as never, '')}
      />
    </div>
  );
}

/** Scaled down as a whole where its place is narrower than its `width` (27, Banner preview, v1.58.39). */
const fit = (width: number) => (figure: HTMLElement | null) => {
  const box = figure?.parentElement;
  if (!box) return;
  const seen = new ResizeObserver(() => void (figure!.style.zoom = String(Math.min(1, box.clientWidth / width))));
  seen.observe(box);
  return () => seen.disconnect();
};

/**
 * The circle's "Phone" preview (27, v1.58.46): Home's banner as a 390 px phone shows it beside the 56 px
 * rail, 334 × 183 (136 px under a 47 px top inset whose band, black at 25 %, holds the clock), the name,
 * the members and the "⋯", no tile; with the circle's own banner alone (v1.58.39).
 */
export function PhonePreview({ client }: { client: MatrixClient }) {
  const card = cardOf(client);
  return (
    <Banner client={client} url={circleBanner(client.getUserId()!) ?? undefined} hook={{ role: 'img', 'aria-label': t('banner.onPhone'), 'data-desyr-banner-preview': 'phone' }}>
      <h2>{card.name}</h2>
      {card.members !== undefined && <p className="banner-members">{t('lobby.members', { n: card.members })}</p>}
      <span className="icon-btn banner-menu"><Icon svg={icons.moreVert} size={20} /></span>
    </Banner>
  );
}

/**
 * The Banner preview (27, v1.58.39; a space's, 18): pictures of the real banners at their real size,
 * captioned, side by side where they fit, else stacked: the side panel's ("Computer", 272 × 96 since v1.58.66) with the
 * name, the members and the "⋯" as drawn there (`card`), or for the circle the phone's Home banner alone
 * (`phone`; 27, v1.58.41). Nothing on them reacts.
 */
export function BannerPreviews({ client, card, phone }: { client: MatrixClient; card?: ReturnType<typeof cardOf>; phone?: ReactNode }) {
  return (
    <div className="banner-previews">
      {card && <figure ref={fit(272)}>
        <Banner client={client} url={card.url} hook={{ role: 'img', 'aria-label': t('banner.onComputer'), 'data-desyr-banner-preview': 'computer' }}>
          <h2>{card.name}</h2>
          {card.members !== undefined && <p className="banner-members">{t('lobby.members', { n: card.members })}</p>}
          <span className="icon-btn banner-menu"><Icon svg={icons.moreVert} size={20} /></span>
        </Banner>
        <figcaption>{t('banner.computer')}</figcaption>
      </figure>}
      {phone && (
        <figure ref={fit(334)}>
          {phone}
          <figcaption>{t('banner.phone')}</figcaption>
        </figure>
      )}
    </div>
  );
}

/**
 * A banner's card (18, Banner; the circle's, 27): the preview, and for those who may, "Upload" (a
 * picture uploaded and set at once) and "Remove" (only with a picture); errors in plain words.
 */
export function BannerCard({ client, preview, has, may, set, hook }: { client: MatrixClient; preview: ReactNode; has: boolean; may: boolean; set: (url?: string) => Promise<unknown>; hook?: string }) {
  const file = useRef<HTMLInputElement>(null);
  const [wrong, setWrong] = useState<string>();
  const action = useAction(async (f?: File) => set(f && (await client.uploadContent(f, { type: f.type })).content_uri), 'save');
  const reason = wrong ?? action.message;
  return (
    <Section title={t('banner.section')}>
      <Card title={preview} hook={hook} error={reason && t('banner.failed', { reason })}>
        {may && (
          <div className="settings-buttons">
            <Button small busy={action.busy} onClick={() => file.current?.click()}>
              {t('settings.upload')}
            </Button>
            {has && (
              <Button small variant="critical-soft" disabled={action.busy} onClick={() => (setWrong(undefined), void action.run())}>
                {t('settings.remove')}
              </Button>
            )}
            <input
              ref={file}
              type="file"
              accept={TYPES.join(',')}
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = '';
                const problem = f && (!TYPES.includes(f.type) ? t('settings.notPicture') : f.size > 10 << 20 ? t('error.fileTooLarge') : undefined);
                setWrong(problem);
                if (f && !problem) void action.run(f);
              }}
            />
          </div>
        )}
      </Card>
    </Section>
  );
}
